import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Box,
    Flex,
    Spinner,
} from '@chakra-ui/react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetSettings } from '../hooks/useGetSettings';
import SessionContext from '../contexts/SessionContext';
import {
    SettingsBreadcrumbActionsProvider,
    SettingsBreadcrumbBar,
    SettingsDetailCrumbProvider,
    SettingsPage,
    type SettingsBreadcrumb,
} from '../components/settings/primitives';
import SettingsSidebar from './settings/SettingsSidebar';
import SettingsOverview from './settings/SettingsOverview';
import SettingsDraftActionBar from './settings/SettingsDraftActionBar';
import { SettingsDraftProvider } from './settings/SettingsDraftContext';
import { findSection, organizationStatesOf, renderSettingsSection } from './settings/sections';
import UserDetailSection from './settings/sections/users/UserDetailSection';
import { ConnectionDetailSection } from '../components/settings';
import AddUsersSection from './settings/sections/users/AddUsersSection';
import VendorDetailSection from './settings/sections/vendors/VendorDetailSection';
import DrpDetailSection from './settings/sections/drp/DrpDetailSection';
import OemDetailSection from './settings/sections/oem/OemDetailSection';
import RequoteRuleDetailSection from './settings/sections/quoting/RequoteRuleDetailSection';
import CccOrderingRuleDetailSection from './settings/sections/ordering/CccOrderingRuleDetailSection';
import PriceMatchOrderSection from './settings/sections/priceMatch/PriceMatchOrderSection';
import PartAllowanceFlowSection from './settings/sections/selection/PartAllowanceFlowSection';
import PricingRuleDetailSection from './settings/sections/invoicing/pricing/PricingRuleDetailSection';
import VendorFlaggingRuleDetailSection from './settings/sections/invoicing/VendorFlaggingRuleDetailSection';
import {
    collectApiSectionKeys,
    getEntryDescription,
    getVisibleNav,
    getVisibleNavByGroup,
    hasFullSettingsAccess,
    type SettingsAccessContext,
} from './settings/settingsNav';

const Settings = () => {
    const session = useContext(SessionContext);
    const { data, isLoading, isError, error, refetch } = useGetSettings();
    const { sectionId: sectionIdParam, userId, connectionId, detailId } = useParams<{
        sectionId?: string;
        userId?: string;
        connectionId?: string;
        detailId?: string;
    }>();
    // The detail routes (/settings/users/:userId, /settings/connections/:connectionId)
    // carry no :sectionId, but they live inside their section, so the sidebar
    // keeps that section active.
    const sectionId = userId ? 'users' : connectionId ? 'connections' : sectionIdParam;
    // /settings/users/new is the add-users flow rather than a user's page; no
    // real user id can be "new", so the segment doubles as the flow's route.
    const isAddUsersFlow = userId === 'new';
    const navigate = useNavigate();

    const accessContext = useMemo<SettingsAccessContext>(
        () => ({
            session,
            settings: data,
            apiSectionKeys: collectApiSectionKeys(data),
        }),
        [session, data],
    );

    const visibleEntries = useMemo(() => getVisibleNav(accessContext), [accessContext]);
    const groups = useMemo(() => getVisibleNavByGroup(accessContext), [accessContext]);
    const hasFullAccess = hasFullSettingsAccess(session);
    const activeEntry = visibleEntries.find((entry) => entry.id === sectionId);

    const [searchQuery, setSearchQuery] = useState('');
    // Set by whichever nested page is open (a user under Users, say) through
    // useSettingsDetailCrumbs, so the trail below gains its levels
    // automatically; a flow contributes one crumb per view it opens.
    const [detailCrumbs, setDetailCrumbs] = useState<SettingsBreadcrumb[] | undefined>(undefined);
    // Right-hand slot of the breadcrumb bar, which nested screens portal their
    // primary actions into (a flow's Cancel/Next/Save).
    const [crumbActionsEl, setCrumbActionsEl] = useState<HTMLDivElement | null>(null);

    // Trail: Settings / <section> / <detail>. Only ancestors are clickable, and
    // the root crumb links to the overview only for users who have one.
    const breadcrumbs = useMemo<SettingsBreadcrumb[]>(() => {
        const items: SettingsBreadcrumb[] = [
            {
                label: 'Settings',
                onClick: hasFullAccess ? () => navigate('/settings') : undefined,
            },
        ];
        if (activeEntry) {
            items.push({
                label: activeEntry.label,
                onClick: () => navigate(`/settings/${activeEntry.id}`),
            });
        }
        if (activeEntry && detailCrumbs) items.push(...detailCrumbs);
        return items;
    }, [activeEntry, detailCrumbs, hasFullAccess, navigate]);

    const handleBack = () => {
        // Recorded by ProtectedRoute whenever the user is outside /settings.
        navigate(sessionStorage.getItem('settingsReturnPath') || '/');
    };

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        // Results live on the overview, so searching from a section jumps there.
        if (sectionId) navigate('/settings');
    };

    // Users without full settings access have no overview to land on, and an
    // unknown or newly hidden section id should not leave a blank page.
    useEffect(() => {
        if (!data || visibleEntries.length === 0) return;
        if (!sectionId) {
            if (!hasFullAccess) navigate(`/settings/${visibleEntries[0].id}`, { replace: true });
            return;
        }
        if (!activeEntry) {
            navigate(hasFullAccess ? '/settings' : `/settings/${visibleEntries[0].id}`, {
                replace: true,
            });
        }
    }, [data, sectionId, activeEntry, visibleEntries, hasFullAccess, navigate]);

    if (isLoading || (!data && !isError)) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <Spinner size="xl" />
            </Box>
        );
    }

    if (isError || !data) {
        return (
            <Box p={8}>
                <Alert status="error">
                    <AlertIcon />
                    <AlertTitle>Error loading settings!</AlertTitle>
                    <AlertDescription>{error?.message || 'An unknown error occurred.'}</AlertDescription>
                </Alert>
            </Box>
        );
    }

    return (
        // Full-screen takeover: ProtectedRoute renders no app chrome under
        // /settings, so this owns the whole viewport. The sidebar spans the
        // full viewport height; the content area is topped by the breadcrumb
        // bar, which every screen below it shares.
        <Flex w="100%" h="100%" overflow="hidden">
            <SettingsSidebar
                groups={groups}
                activeId={activeEntry?.id}
                onSelect={(id) => navigate(`/settings/${id}`)}
                onBack={handleBack}
                onOverviewSelect={hasFullAccess ? () => navigate('/settings') : undefined}
                isOverviewActive={hasFullAccess && !sectionId}
                searchQuery={hasFullAccess ? searchQuery : undefined}
                onSearchChange={hasFullAccess ? handleSearchChange : undefined}
            />

            <Flex direction="column" flex={1} minW={0} minH={0} overflow="hidden" bg="white">
                <SettingsBreadcrumbBar items={breadcrumbs} actionsRef={setCrumbActionsEl} />

                <Box flex={1} minH={0} position="relative" overflow="hidden">
                    <SettingsBreadcrumbActionsProvider container={crumbActionsEl}>
                        <SettingsDetailCrumbProvider onCrumbsChange={setDetailCrumbs}>
                            {activeEntry && activeEntry.id === 'oem' && detailId ? (
                                // OEM screen: add and edit share the same
                                // step-by-step flow, with its own save path
                                // (POST /settings/update).
                                (() => {
                                    const oemSection = findSection(data, 'oem');
                                    if (!oemSection) return null;
                                    return (
                                        <OemDetailSection
                                            key={detailId}
                                            oemId={detailId}
                                            oems={oemSection.data}
                                            oemOptions={data.oemOptions}
                                            refetch={refetch}
                                        />
                                    );
                                })()
                            ) : activeEntry && activeEntry.id === 'drp' && detailId ? (
                                // DRP carrier screen: add and edit share the
                                // same step-by-step flow, with its own save
                                // path (POST /settings/update).
                                (() => {
                                    const insuranceSection = findSection(data, 'insurance');
                                    if (!insuranceSection) return null;
                                    const rekeySection = findSection(data, 'rekey');
                                    return (
                                        <DrpDetailSection
                                            key={detailId}
                                            drpId={detailId}
                                            insurance={insuranceSection.data}
                                            rekeySettings={rekeySection?.data}
                                            managementSystem={rekeySection?.managementSystem}
                                            refetch={refetch}
                                        />
                                    );
                                })()
                            ) : activeEntry && activeEntry.id === 'vendors' && detailId ? (
                                // Vendor screen: add and edit are the same
                                // full-screen split view, with its own save
                                // path (POST /settings/update).
                                (() => {
                                    const vendorSection = findSection(data, 'vendor');
                                    if (!vendorSection) return null;
                                    return (
                                        <VendorDetailSection
                                            key={detailId}
                                            vendorId={detailId}
                                            vendors={vendorSection.data}
                                            oemOptions={data.oemOptions}
                                            refetch={refetch}
                                        />
                                    );
                                })()
                            ) : activeEntry && activeEntry.id === 'quoting' && detailId ? (
                                // Re-quote rule screen: add and edit share the
                                // same split view, with its own save path
                                // (POST /settings/update).
                                (() => {
                                    const quotingSection = findSection(data, 'quoting');
                                    if (!quotingSection) return null;
                                    const { hasCl, hasPt, ...quotingData } = quotingSection.data;
                                    return (
                                        <RequoteRuleDetailSection
                                            key={detailId}
                                            ruleId={detailId}
                                            quoting={quotingData}
                                            oemOptions={data.oemOptions}
                                            refetch={refetch}
                                        />
                                    );
                                })()
                            ) : activeEntry && activeEntry.id === 'ordering' && detailId ? (
                                // Custom CCC ordering rule: add and edit share
                                // the same split view, with its own save path
                                // (POST /settings/update).
                                (() => {
                                    const orderingSection = findSection(data, 'ordering');
                                    if (!orderingSection) return null;
                                    return (
                                        <CccOrderingRuleDetailSection
                                            key={detailId}
                                            ruleId={detailId}
                                            ordering={orderingSection.data}
                                            oemOptions={data.oemOptions}
                                            refetch={refetch}
                                        />
                                    );
                                })()
                            ) : activeEntry &&
                              activeEntry.id === 'selection' &&
                              detailId === 'price-match' ? (
                                // Custom price matching order: the ranking the
                                // Selection section used to open as a modal,
                                // with its own save path (POST /settings/update).
                                (() => {
                                    const selectionSection = findSection(data, 'selection');
                                    if (!selectionSection) return null;
                                    return (
                                        <PriceMatchOrderSection
                                            data={selectionSection.data}
                                            refetch={refetch}
                                        />
                                    );
                                })()
                            ) : activeEntry &&
                              activeEntry.id === 'part-allowances' &&
                              detailId ? (
                                // Add parts to one allowance list, or reopen a
                                // saved rule: the part catalog modal turned into
                                // a step-by-step flow with its own save path
                                // (POST /settings/update).
                                (() => {
                                    const selectionSection = findSection(data, 'selection');
                                    if (!selectionSection) return null;
                                    return (
                                        <PartAllowanceFlowSection
                                            key={detailId}
                                            listSlug={detailId}
                                            data={selectionSection.data}
                                            oemOptions={data.oemOptions}
                                            refetch={refetch}
                                        />
                                    );
                                })()
                            ) : activeEntry &&
                              activeEntry.id === 'invoicing-flags' &&
                              detailId &&
                              detailId.startsWith('flag-rule') ? (
                                (() => {
                                    const invoicingSection = findSection(data, 'invoicing');
                                    if (!invoicingSection) return null;
                                    return (
                                        <VendorFlaggingRuleDetailSection
                                            key={detailId}
                                            rowId={detailId}
                                            data={invoicingSection.data}
                                            refetch={refetch}
                                        />
                                    );
                                })()
                            ) : activeEntry &&
                              (activeEntry.id === 'invoicing-markups' ||
                                  activeEntry.id === 'invoicing-discounts' ||
                                  activeEntry.id === 'invoicing-flags') &&
                              detailId ? (
                                // Markup, discount and discount flag rows: add
                                // and edit share one full-screen rule editor,
                                // with its own save path (POST
                                // /settings/update).
                                (() => {
                                    const invoicingSection = findSection(data, 'invoicing');
                                    if (!invoicingSection) return null;
                                    return (
                                        <PricingRuleDetailSection
                                            key={detailId}
                                            sectionId={activeEntry.id}
                                            rowId={detailId}
                                            data={invoicingSection.data}
                                            organizationStates={organizationStatesOf(data)}
                                            refetch={refetch}
                                        />
                                    );
                                })()
                            ) : activeEntry && activeEntry.id === 'users' && isAddUsersFlow ? (
                                // Multi-step add-users flow. It owns its own save
                                // path (POST /settings/user/invite per row), so like the user
                                // detail page it skips the settings draft provider.
                                (() => {
                                    const userSection = findSection(data, 'user');
                                    if (!userSection) return null;
                                    return (
                                        <AddUsersSection
                                            defaultPermissions={userSection.defaultPermission}
                                            refetch={refetch}
                                        />
                                    );
                                })()
                            ) : activeEntry && activeEntry.id === 'users' && userId ? (
                                // User detail page: its own save path (useEditUser),
                                // so it skips the settings draft provider and the
                                // SettingsPage title chrome. Its breadcrumb crumb
                                // comes from useSettingsDetailCrumb.
                                (() => {
                                    const userSection = findSection(data, 'user');
                                    if (!userSection) return null;
                                    return (
                                        <UserDetailSection
                                            key={userId}
                                            userId={userId}
                                            users={userSection.data}
                                            defaultPermissions={userSection.defaultPermission}
                                            refetch={refetch}
                                        />
                                    );
                                })()
                            ) : activeEntry && activeEntry.id === 'connections' && connectionId ? (
                                // A single connection's credentials. Saves go through
                                // the integrations endpoints rather than the settings
                                // draft, so it skips the draft provider too.
                                <ConnectionDetailSection
                                    key={connectionId}
                                    connectionId={connectionId}
                                />
                            ) : activeEntry ? (
                                // Keyed by section so unsaved edits never follow the
                                // user from one section into the next.
                                <SettingsDraftProvider key={activeEntry.id}>
                                    <SettingsPage
                                        title={activeEntry.label}
                                        description={getEntryDescription(activeEntry, accessContext)}
                                        badge={activeEntry.badge}
                                        variant={activeEntry.pageVariant ?? 'form'}
                                    >
                                        {renderSettingsSection(activeEntry.id, {
                                            settings: data,
                                            refetch,
                                            session,
                                        })}
                                    </SettingsPage>
                                    <SettingsDraftActionBar />
                                </SettingsDraftProvider>
                            ) : (
                                hasFullAccess && (
                                    <SettingsOverview
                                        groups={groups}
                                        accessContext={accessContext}
                                        query={searchQuery}
                                        onSelect={(id) => navigate(`/settings/${id}`)}
                                    />
                                )
                            )}
                        </SettingsDetailCrumbProvider>
                    </SettingsBreadcrumbActionsProvider>
                </Box>
            </Flex>
        </Flex>
    );
};

export default Settings;
